import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CharonHttpService, IInitializeParam } from '@quickweb/connect-angular';
import { environment } from 'src/environments/environment';
import { Observable, from, map, retry, switchMap } from 'rxjs';
import { BodyResponseBFF, ResponseBFF } from '../states/interface/reponse-caronte-bff.interface';
import { Router } from '@angular/router';
import { APLICACAO_ROTAS_TRADICIONAL } from '../../constants/aplicacao-rotas-tradicional.const';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'HEAD';

type Rel =
  | 'consultarCep'
  | 'deletarProponente'
  | 'cadastrarProponentes'
  | 'entrypoint'
  | 'consultarPropostaPorIdPessoa'
  | 'consultarPropostaPorId'
  | 'atualizarProponente'
  | 'obterProdutos'
  | 'contratarProdutos'
  | 'consultarSexo'
  | 'consultarOrigemRenda'
  | 'consultarOcupacao'
  | 'consultarNacionalidade'
  | 'enviarDocumento'
  | 'consultarDocumentos'
  | 'deletarDocumentoPorId'
  | 'consultarPaises'
  | 'consultarUnidadesFederativas'
  | 'cancelarProposta'
  | 'consultarProponentes'
  | 'statusAssinatura'
  | 'alterarTipoAssinatura'
  | 'downloadPAC'
  | 'atualizarEstadoProposta'
  | 'consultarCidadesPorUF'
  | 'cadastrarProcurador'
  | 'consultarProcuradorPorId'
  | 'consultarProcuradores'
  | 'atualizarProcurador'
  | 'deletarProcurador'
  | 'reaproveitarDocumento';

@Injectable()
export class RequestCaronteService {
  private startCarontePromise: Promise<void>;
  constructor(private charon: CharonHttpService, private router: Router) {
    this.startCarontePromise = this.initializeCaronte();
  }

  /**
  * Configura e inicializa o serviço Charon com os parâmetros necessários.
  * Configura os cabeçalhos HTTP e as configurações de criptografia.
  */

  private initializeCaronte(): Promise<void> {
    return new Promise((resolve) => {
      const token = this.getGlobalToken();
      this.charon.config({
        entryPoint: environment.baseCharonUrl,
        httpOptions: {
          headers: new HttpHeaders({
            'x-itau-apikey': window.clientId || 'apikey',
            Authorization: "Bearer " + token || '',
            'x-itau-user-agent': window.navigator.userAgent,
          }),
        },
      });

      this.charon.setCryptography(environment.charonCripto);
      const parameters: IInitializeParam = {
        entryPoint: environment.baseCharonUrl,
        charonID: environment.charonID,
      };

      this.charon.initialize(parameters).subscribe({
        next: () => {
          resolve();
        },
        error: () => {
          this.router.navigate([APLICACAO_ROTAS_TRADICIONAL.statusEncerrada.rota]);
          resolve();
        }
      });
    });
  }

  private waitForStartCaronte(): Observable<void> {
    return from(this.startCarontePromise);
  }

  /**
 * Faz uma requisição usando o serviço Charon.
 * @param {Rel} path - O endpoint da API a ser chamado.
 * @param {HttpMethod} methodo - O método HTTP a ser utilizado.
 * @param {any} [body] - O corpo da requisição, se aplicável.
 * @param {Array<{ chave: string, valor: string }>} [parametros] - Parâmetros de consulta para a requisição.
 * @returns {Observable<T>} Um observable da resposta.getCaronte
 */
  public requestCaronte<T>(
    path: Rel,
    methodo: HttpMethod,
    body?: any,
    parametros?: Array<{ chave: string, valor: string }>
  ): Observable<T | any> {
    return this.waitForStartCaronte().pipe(
      switchMap(() => {
        const httpParams = this.buildHttpParams(parametros);
        return this.charon.request<any, T>(methodo, path, {
          body,
          params: httpParams,
          headers: new HttpHeaders({
            'x-itau-apikey': crypto.randomUUID(),
            'x-itau-correlationID': crypto.randomUUID(),
            Authorization: "Bearer " + this.getGlobalToken(),
            'Content-Type': 'application/json;charset=UTF-8'
          }),
          charonID: environment.charonID
        });
      }),
      retry({ count: 1, delay: 1000 }),
    );
  }

  /**
 * Realiza uma requisição GET usando o serviço Charon.
 * @param {Rel} path - O endpoint da API a ser chamado.
 * @param {Array<{ chave: string, valor: string }>} [parametros] - Parâmetros de consulta para a requisição.
 * @returns {Observable<T>} Um observable da resposta.
 */
  public getCaronte<T>(path: Rel, parametros?: Array<{ chave: string, valor: string }>): Observable<BodyResponseBFF<T>> {
    return this.waitForStartCaronte().pipe(
      switchMap(() => {
        const httpParams = this.buildHttpParams(parametros);
        return this.charon.get<ResponseBFF<T>>(path, {
          params: httpParams, charonID: environment.charonID,
          headers: new HttpHeaders({
            Authorization: "Bearer " + this.getGlobalToken(),
            'x-itau-apikey': crypto.randomUUID(),
            'x-itau-correlationID': crypto.randomUUID(),
            'Content-Type': 'application/json;charset=UTF-8'
          }),

        }).pipe(
          map((response) => response.body),
          retry({ count: 1, delay: 1000 }),
        );
      })
    );
  }

  /**
 * Realiza uma requisição POST usando o serviço Charon.
 * @param {Rel} path - O endpoint da API a ser chamado.
 * @param {any} [body] - O corpo da requisição.
 * @returns {Observable<BodyResponseBFF<T>>} Um observable da resposta.
 */
  public postCaronte<T>(path: Rel, body?: any, parametros?: Array<{ chave: string, valor: string }>): Observable<BodyResponseBFF<T>> {
    return this.waitForStartCaronte().pipe(
      switchMap(() => {
        const httpParams = this.buildHttpParams(parametros);
        return this.charon.post<ResponseBFF<T>>(path, body, {
          params: httpParams, charonID: environment.charonID,
          headers: new HttpHeaders({
            Authorization: "Bearer " + this.getGlobalToken(),
            'x-itau-apikey': crypto.randomUUID(),
            'x-itau-correlationID': crypto.randomUUID(),
            'Content-Type': 'application/json;charset=UTF-8'
          }),
        }).pipe(
          map((response) => response.body),
          retry({ count: 1, delay: 1000 }),
        );
      })
    );
  }

  /**
   * Realiza uma requisição DELETE usando o serviço Charon.
   * @param {Rel} path - O endpoint da API a ser chamado.
   * @param {any} [body] - O corpo da requisição, se aplicável.
   * @returns {Observable<BodyResponseBFF<T>>} Um observable da resposta.
   */
  public deleteCaronte<T>(path: Rel, body?: any, parametros?: Array<{ chave: string, valor: string }>): Observable<BodyResponseBFF<T>> {
    return this.waitForStartCaronte().pipe(
      switchMap(() => {
        const httpParams = this.buildHttpParams(parametros);
        return this.charon.delete<ResponseBFF<T>>(path, {
          params: httpParams, body: body, charonID: environment.charonID,
          headers: new HttpHeaders({
            Authorization: "Bearer " + this.getGlobalToken(),
            'x-itau-apikey': crypto.randomUUID(),
            'x-itau-correlationID': crypto.randomUUID(),
            'Content-Type': 'application/json;charset=UTF-8'
          }),
        }).pipe(
          map((response) => response.body),
          retry({ count: 1, delay: 1000 }),
        );
      })
    );
  }
  /**
    * Realiza uma requisição PUT usando o serviço Charon.
    * @param {Rel} path - O endpoint da API a ser chamado.
    * @param {any} [body] - O corpo da requisição.
    * @returns {Observable<T>} Um observable da resposta.
    */
  public putCaronte<T>(path: Rel, body?: any): Observable<BodyResponseBFF<T>> {
    return this.waitForStartCaronte().pipe(
      switchMap(() => {
        return this.charon.put<ResponseBFF<T>>(path, { body }, {
          charonID: environment.charonID,
          headers: new HttpHeaders({
            Authorization: "Bearer " + this.getGlobalToken(),
            'x-itau-apikey': crypto.randomUUID(),
            'x-itau-correlationID': crypto.randomUUID(),
            'Content-Type': 'application/json;charset=UTF-8'
          }),
        }).pipe(
          map((response) => response.body),
          retry({ count: 1, delay: 1000 }),
        );
      })
    );
  }

  /**
    * Recupera o token global do objeto window.
    * @returns {string} O token global ou uma string vazia se não encontrado.
    */

  public getGlobalToken(): string {
    return sessionStorage.getItem('token') || '';
  }


  /**
 * Constrói parâmetros HTTP a partir de um array de pares chave-valor.
 * @param {Array<{ chave: string, valor: string }>} [parametros] - Os parâmetros a serem convertidos.
 * @returns {HttpParams} O objeto HttpParams construído.
 */

  private buildHttpParams(parametros?: Array<{ chave: string, valor: string }>): HttpParams {
    let httpParams = new HttpParams();
    if (parametros) {
      parametros.forEach((param) => {
        httpParams = httpParams.set(param.chave, param.valor);
      });
    }
    return httpParams;
  }
}
